import { ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";

/**
 * Two-way `open` state for a filter that exposes a string/boolean `open`
 * attribute and emits a camelCase `update:open` event. Seeds from the prop,
 * mirrors later prop changes inwards, and emits outwards when the local state
 * changes — the `ref(toBoolean(props.open)) + watch(prop) + watch(open)` pattern
 * every filter activator repeats.
 *
 * @param {() => (Boolean|String|undefined)} getOpen - getter for the `open` prop
 * @param {(event: String, value: Boolean) => void} emit - the component's emit
 * @param {{ event?: String }} [options] - the emit event name (defaults to "update:open")
 * @returns {import("vue").Ref<Boolean>} the local open ref
 */
export function useControllableOpen(getOpen, emit, { event = "update:open" } = {}) {
  const open = ref(toBoolean(getOpen()));

  watch(getOpen, (value) => {
    open.value = toBoolean(value);
  });

  watch(open, (value) => {
    emit(event, value);
  });

  return open;
}
